import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../../../app/api/api';
import { MealItemDetailsData } from '../../../app/data/models/mealItems/mealItemDetailsData';
import { MealItemCustomization } from '../../../app/data/models/mealItems/mealItemCustomization';

export interface MealItemDetailsState {
  mealItem: MealItemDetailsData | null;
  linkedCustomizations: MealItemCustomization[];
  status: string;
}

const initialState: MealItemDetailsState = {
  mealItem: null,
  linkedCustomizations: [],
  status: 'idle',
};

export const fetchMealItemDetailsAsync = createAsyncThunk<
  MealItemDetailsData,
  { restaurantId: string; mealItemId: string }
>('mealItem/fetchDetails', async ({ restaurantId, mealItemId }, thunkAPI) => {
  try {
    return await api.MealItem.details(restaurantId, mealItemId);
  } catch (error: unknown) {
    if (error instanceof Error) {
      return thunkAPI.rejectWithValue({ error: error.message });
    }
    return thunkAPI.rejectWithValue({ error: 'An unknown error occurred' });
  }
});

export const fetchLinkedCustomizationsAsync = createAsyncThunk<
  MealItemCustomization[],
  { restaurantId: string; mealItemId: string }
>(
  'mealItem/fetchLinkedCustomizations',
  async ({ restaurantId, mealItemId }, thunkAPI) => {
    try {
      return await api.MealItemCustomization.getLinked(
        restaurantId,
        mealItemId
      );
    } catch (error: unknown) {
      if (error instanceof Error) {
        return thunkAPI.rejectWithValue({ error: error.message });
      }
      return thunkAPI.rejectWithValue({ error: 'An unknown error occurred' });
    }
  }
);

export const addCustomizationToMealItemAsync = createAsyncThunk<
  void,
  { restaurantId: string; mealItemId: string; customizationId: string }
>(
  'mealItem/addCustomization',
  async ({ restaurantId, mealItemId, customizationId }, thunkAPI) => {
    try {
      await api.MealItemCustomization.addToMealItem(
        restaurantId,
        mealItemId,
        customizationId
      );
      await thunkAPI.dispatch(
        fetchLinkedCustomizationsAsync({ restaurantId, mealItemId })
      );
    } catch (error: unknown) {
      if (error instanceof Error) {
        return thunkAPI.rejectWithValue({ error: error.message });
      }
      return thunkAPI.rejectWithValue({ error: 'An unknown error occurred' });
    }
  }
);

export const removeCustomizationFromMealItemAsync = createAsyncThunk<
  void,
  { restaurantId: string; mealItemId: string; customizationId: string }
>(
  'mealItem/removeCustomization',
  async ({ restaurantId, mealItemId, customizationId }, thunkAPI) => {
    try {
      await api.MealItemCustomization.removeFromMealItem(
        restaurantId,
        mealItemId,
        customizationId
      );
      await thunkAPI.dispatch(
        fetchLinkedCustomizationsAsync({ restaurantId, mealItemId })
      );
    } catch (error: unknown) {
      if (error instanceof Error) {
        return thunkAPI.rejectWithValue({ error: error.message });
      }
      return thunkAPI.rejectWithValue({ error: 'An unknown error occurred' });
    }
  }
);

export const mealItemDetailsSlice = createSlice({
  name: 'mealItemDetails',
  initialState,
  reducers: {
    clearMealItemDetails: (state) => {
      state.mealItem = null;
      state.linkedCustomizations = [];
      state.status = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMealItemDetailsAsync.pending, (state) => {
        state.status = 'pendingFetchDetails';
      })
      .addCase(fetchMealItemDetailsAsync.fulfilled, (state, action) => {
        state.mealItem = action.payload;
        state.status = 'idle';
      })
      .addCase(fetchMealItemDetailsAsync.rejected, (state) => {
        state.status = 'idle';
      })
      .addCase(fetchLinkedCustomizationsAsync.pending, (state) => {
        state.status = 'pendingFetchCustomizations';
      })
      .addCase(fetchLinkedCustomizationsAsync.fulfilled, (state, action) => {
        state.linkedCustomizations = action.payload;
        state.status = 'idle';
      })
      .addCase(fetchLinkedCustomizationsAsync.rejected, (state) => {
        state.status = 'idle';
      })
      .addCase(addCustomizationToMealItemAsync.pending, (state) => {
        state.status = 'pendingAddCustomization';
      })
      .addCase(addCustomizationToMealItemAsync.fulfilled, (state) => {
        state.status = 'idle';
      })
      .addCase(removeCustomizationFromMealItemAsync.pending, (state) => {
        state.status = 'pendingRemoveCustomization';
      })
      .addCase(removeCustomizationFromMealItemAsync.fulfilled, (state) => {
        state.status = 'idle';
      });
  },
});

export const { clearMealItemDetails } = mealItemDetailsSlice.actions;
